import React, { useState, useContext, useEffect } from "react";
import { refetchCompanyData } from "./Components/utils";
import NetworkService from "./NetworkService";
import { AdminPayload } from "./types/admin";
import { CompanyData } from "./types/company";

type UserState = AdminPayload | null;
type CompanyDataState = CompanyData | null;
type UserTuple = [UserState, (value: UserState) => void];
type CompanyDataTuple = [CompanyDataState, React.Dispatch<React.SetStateAction<CompanyDataState>>];
type BurgerTuple = [boolean, React.Dispatch<React.SetStateAction<boolean>>];

export interface AppContextValue {
  user: UserTuple;
  openBurger: BurgerTuple;
  companyData: CompanyDataTuple;
}

export const AppContext = React.createContext<AppContextValue | undefined>(undefined);

const userFromLS = (() => {
  try {
    const rawUser = localStorage.getItem("user");
    return rawUser ? (JSON.parse(rawUser) as AdminPayload) : null;
  } catch {
    return null;
  }
})();

export function GlobalContextProvider({ children }: React.PropsWithChildren) {
  const [user, setUser] = useState<AdminPayload | null>(userFromLS);
  const [openBurger, setOpenBurger] = useState<boolean>(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);

  const updateUser = (value: AdminPayload | null) => {
    setUser(value);

    if (value) {
      localStorage.setItem("user", JSON.stringify(value));
      NetworkService.setAuthToken(value.authorisation?.token);
      return;
    }

    localStorage.removeItem("user");
    NetworkService.setAuthToken(null);
  };

  useEffect(() => {
    if (user) {
      refetchCompanyData(setCompanyData);
    }
  }, [user]);

  return (
    <AppContext.Provider
      value={{
        user: [user, updateUser],
        openBurger: [openBurger, setOpenBurger],
        companyData: [companyData, setCompanyData],
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useGlobalContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useGlobalContext must be used within GlobalContextProvider");
  }
  return context;
}
